/**
 * Dashboard → PBX Dashboard.
 *
 * Telephony metrics (trunks, extensions, queues, answer rates) are not wired
 * to an API yet — this is the designed shell the live figures will drop into:
 * the same card language as My Dashboard so the page doesn't read as an
 * afterthought while the backend lands.
 */
import { type JSX } from 'react'
import { Gauge, Headset, Network, PhoneCall, Server, Timer } from 'lucide-react'

const CARD =
  'rounded-[3px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]'

/** Placeholder metric well — label + dashed value while the API is pending. */
function PbxWell({ label, hint, Icon }: { label: string; hint: string; Icon: typeof Server }): JSX.Element {
  return (
    <div className={`${CARD} p-5`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
        <span className="flex h-9 w-9 items-center justify-center rounded-[3px] bg-indigo-50 text-indigo-600">
          <Icon className="h-[18px] w-[18px]" />
        </span>
      </div>
      <p className="mt-4 font-mono text-3xl font-extrabold tabular-nums tracking-tight text-slate-300">—</p>
      <p className="mt-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-inset ring-slate-200">
        {hint}
      </p>
    </div>
  )
}

export default function PBXDashboardPage(): JSX.Element {
  return (
    <div className="min-h-full bg-slate-50 p-6">
      {/* Header — same card language as My Dashboard */}
      <section className={`${CARD} px-5 py-4`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold tracking-tight text-[#0f172a]">PBX Dashboard</h1>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                <Network className="h-3 w-3" />
                Telephony
              </span>
            </div>
            <p className="text-[12px] text-slate-500">
              Trunks, extensions and queue health across the phone system
            </p>
          </div>
        </div>
      </section>

      {/* Stat wells — waiting on the telephony API */}
      <section className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PbxWell label="Calls today" hint="Coming soon" Icon={PhoneCall} />
        <PbxWell label="Answer rate" hint="Coming soon" Icon={Gauge} />
        <PbxWell label="Extensions online" hint="Coming soon" Icon={Headset} />
        <PbxWell label="Avg wait in queue" hint="Coming soon" Icon={Timer} />
      </section>

      {/* What's planned */}
      <section className={`${CARD} mt-5`}>
        <div className="flex flex-col items-center px-6 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-[3px] border border-indigo-100/80 bg-indigo-50 text-indigo-600">
            <Server className="h-6 w-6" />
          </span>
          <h2 className="mt-4 text-base font-bold tracking-tight text-[#0f172a]">
            The PBX dashboard is being built
          </h2>
          <p className="mx-auto mt-1.5 max-w-md text-[12.5px] leading-relaxed text-slate-500">
            This panel will show live trunk health, registered extensions, queue depth, and
            answer/abandon rates for the phone system. Call-level detail is already available under{' '}
            <span className="font-semibold text-slate-700">Contact Center Report → Agent Reports</span>{' '}
            and <span className="font-semibold text-slate-700">Reports → Call Reports</span>.
          </p>
        </div>
      </section>
    </div>
  )
}
